import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'tertiary';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...props
}) => {
  const baseStyles =
    'inline-flex items-center justify-center rounded-md font-label font-bold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]';

  const variants: Record<string, string> = {
    primary:
      'bg-primary text-on-primary shadow-level1 hover:bg-primary-container active:bg-primary active:shadow-none transition-all',
    secondary:
      'bg-secondary-container text-on-secondary-container shadow-level1 hover:bg-secondary hover:text-on-secondary active:bg-secondary-container active:shadow-none transition-all',
    tertiary: 'bg-transparent text-primary hover:bg-surface-container-low font-bold',
    outline:
      'bg-transparent text-on-surface hover:bg-surface-container-low outline-variant/15 outline-1 outline focus:outline-primary/20',
    ghost: 'hover:bg-surface-container-low text-on-surface',
  };

  const sizes: Record<string, string> = {
    sm: 'h-8 px-4 text-xs',
    md: 'h-12 px-6 text-sm',
    lg: 'h-14 px-8 text-base',
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};
